import { useContext, createContext, useState } from "react";

import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import {
    getZcLoanCollateral,
    useUnwindQuoteAndIxs,
    getZcCollateral,
    LoopRouteResponseExpanded,
    AbfLoanExpanded,
    getZcLedger,
    LoopPositionExpanded,
    useWindQuoteAndIxs,
    calculateHealthRatio,
    getZcLoanLiquidationThreshold,
    isManageLeverageEligable,
    LoanCollateralExpanded
} from "@bridgesplit/abf-react";
import { lamportsToUiAmount, percentUiToDecimals, bsMath } from "@bridgesplit/utils";
import { DispatchType } from "@bridgesplit/react";
import { LoopWindType, TokenListMetadata } from "@bridgesplit/abf-sdk";

import { DEFAULT_SLIPPAGE } from "../../../common";

interface ManageLeverageForm {
    multiplier: number | undefined;
    increasePrincipalAmount: number | undefined;
    repayWithPrincipalAmount: number | undefined;
    repayWithCollateralAmount: number | undefined;
    isFullRepay: boolean;
    slippagePercentDecimals: number | undefined;
}

const MIN_HEALTH_RATIO = 0.05;

interface ManageLeverageContext {
    // Quote data and loading states
    repayWithCollateralQuote: LoopRouteResponseExpanded | undefined;
    fullRepayWithCollateralQuoteLoading: boolean;
    increaseLeverageQuote: LoopRouteResponseExpanded | undefined;
    repayWithCollateralQuoteLoading: boolean;
    repayWithCollateralQuoteError: FetchBaseQueryError | SerializedError | undefined;
    fullRepayWithCollateralQuoteError: boolean;
    increaseLeverageQuoteLoading: boolean;
    increaseLeverageQuoteError: FetchBaseQueryError | SerializedError | undefined;
    validHealthRepayQuote: boolean;
    validHealthIncreaseLeverageQuote: boolean;

    // Form state management
    form: ManageLeverageForm;
    setForm: DispatchType<ManageLeverageForm>;
    repayType: LoopWindType;
    setRepayType: DispatchType<LoopWindType>;

    // Collateral repay eligibility and computed amounts
    fullRepayAmountInCollateral: number;
    fullRepayAmountInPrincipalFromCollateralRepayment: number;
    customRepayAmountInCollateral: number;
    customRepayAmountInPrincipalFromCollateralRepayment: number;

    // Execution details
    executionDetailsInputs: ExecutionDetailsInputs | undefined;
}

const ManageLeverageContext = createContext<ManageLeverageContext | null>(null);

export function useManageLeverageContext(): ManageLeverageContext {
    const context = useContext(ManageLeverageContext);
    if (context === null) {
        throw new Error("useManageLeverageContext must be used within a ManageLeverageProvider");
    }
    return context;
}

export function ManageLeverageProvider({
    children,
    loanExpanded,
    loopPosition,
    isRepayContext
}: {
    children: React.ReactNode;
    loanExpanded: AbfLoanExpanded | undefined;
    loopPosition: LoopPositionExpanded | undefined;
    isRepayContext: boolean;
}) {
    const [repayType, setRepayType] = useState<LoopWindType>(
        isRepayContext ? LoopWindType.Principal : LoopWindType.Collateral
    );
    const [form, setForm] = useState<ManageLeverageForm>({
        multiplier: undefined,
        increasePrincipalAmount: undefined,
        repayWithPrincipalAmount: undefined,
        repayWithCollateralAmount: undefined,
        isFullRepay: false,
        slippagePercentDecimals: undefined
    });

    const collateral = getZcLoanCollateral(loanExpanded);
    const manageLeverageEligable = isManageLeverageEligable(loanExpanded);

    const {
        // full repay quote - just used for the max repay amount shown in the balance for the repay with collateral form
        fullRepayWithCollateralQuote,
        fullRepayWithCollateralQuoteError,
        fullRepayWithCollateralQuoteLoading,

        // custom repay quote - uses user input to determine the amount of principal to repay
        repayWithCollateralQuote,
        repayWithCollateralQuoteLoading,
        repayWithCollateralQuoteError,

        // increase leverage quote - uses user input to determine the amount of principal to borrow
        increaseLeverageQuote,
        increaseLeverageQuoteLoading,
        increaseLeverageQuoteError,

        // valid health quotes
        validHealthRepayQuote,
        validHealthIncreaseLeverageQuote,

        // Execution details
        executionDetailsInputs
    } = useIncreaseDecreaseLeverageQuotes({
        loanExpanded,
        loopPosition,
        isRepayContext,
        manageLeverageEligable,
        form
    });

    const fullRepayAmountInCollateral = lamportsToUiAmount(
        fullRepayWithCollateralQuote?.inputAmount ?? 0,
        collateral?.metadata.decimals
    );

    const fullRepayAmountInPrincipalFromCollateralRepayment = lamportsToUiAmount(
        fullRepayWithCollateralQuote?.minOutputAmount ?? 0,
        loanExpanded?.principalMetadata.decimals
    );

    const customRepayAmountInCollateral = lamportsToUiAmount(
        repayWithCollateralQuote?.inputAmount ?? 0,
        collateral?.metadata.decimals
    );

    const customRepayAmountInPrincipalFromCollateralRepayment = lamportsToUiAmount(
        repayWithCollateralQuote?.minOutputAmount ?? 0,
        loanExpanded?.principalMetadata.decimals
    );

    return (
        <ManageLeverageContext.Provider
            value={{
                // Quote data and loading states
                repayWithCollateralQuote,
                fullRepayWithCollateralQuoteLoading,
                increaseLeverageQuote,
                repayWithCollateralQuoteLoading,
                repayWithCollateralQuoteError,
                fullRepayWithCollateralQuoteError,
                increaseLeverageQuoteLoading,
                increaseLeverageQuoteError,
                validHealthRepayQuote,
                validHealthIncreaseLeverageQuote,

                // Form state management
                form,
                setForm,
                repayType,
                setRepayType,

                // Collateral repay eligibility and computed amounts
                fullRepayAmountInCollateral,
                fullRepayAmountInPrincipalFromCollateralRepayment,
                customRepayAmountInCollateral,
                customRepayAmountInPrincipalFromCollateralRepayment,

                // Execution details
                executionDetailsInputs
            }}
        >
            {children}
        </ManageLeverageContext.Provider>
    );
}

function useIncreaseDecreaseLeverageQuotes({
    loanExpanded,
    loopPosition,
    isRepayContext,
    manageLeverageEligable,
    form
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    loopPosition: LoopPositionExpanded | undefined;
    isRepayContext: boolean;
    manageLeverageEligable: boolean;
    form: ManageLeverageForm;
}) {
    const ledger = getZcLedger(loanExpanded);
    const collateral = getZcCollateral(loanExpanded);
    const repayWithCollateralAmount = form.repayWithCollateralAmount;
    const increasePrincipalAmount = form.increasePrincipalAmount;

    // this is used to determine the max amount of collateral needed to close out the debt
    // its used in the set max fn in the repay with collateral form
    const {
        data: fullRepayWithCollateralQuote,
        error: fullRepayWithCollateralQuoteError,
        isLoading: fullRepayWithCollateralQuoteLoading,
        isFetching: fullRepayWithCollateralQuoteIsFetching
    } = useUnwindQuoteAndIxs({
        loopPositionExpanded: loopPosition,
        loanExpanded,
        debtToRepay: ledger?.ledgerDebt.total,
        loanCollateral: getZcCollateral(loanExpanded),
        slippagePercentDecimals: form.slippagePercentDecimals ?? percentUiToDecimals(DEFAULT_SLIPPAGE),
        windType: LoopWindType.Collateral,
        collateralAmount: collateral?.amount,
        skip: !manageLeverageEligable || form.slippagePercentDecimals === undefined
    });

    const skipCustomRepayWithCollateralQuote = !manageLeverageEligable || !repayWithCollateralAmount;

    const {
        data: repayWithCollateralQuote,
        isLoading: repayWithCollateralQuoteLoading,
        isFetching: repayWithCollateralQuoteIsFetching,
        error: repayWithCollateralQuoteError
    } = useUnwindQuoteAndIxs({
        loopPositionExpanded: loopPosition,
        loanExpanded,
        debtToRepay: 0, // just looking for the swap price here
        collateralAmount: repayWithCollateralAmount,
        loanCollateral: getZcCollateral(loanExpanded),
        slippagePercentDecimals: form.slippagePercentDecimals ?? percentUiToDecimals(DEFAULT_SLIPPAGE),
        windType: LoopWindType.Principal,
        skip: skipCustomRepayWithCollateralQuote || form.slippagePercentDecimals === undefined
    });

    // increase leverage quote
    const {
        data: increaseLeverageQuote,
        isLoading: increaseLeverageQuoteLoading,
        isFetching: increaseLeverageQuoteIsFetching,
        error: increaseLeverageQuoteError
    } = useWindQuoteAndIxs({
        loopExpanded: loopPosition?.loopExpanded,
        principalAmount: increasePrincipalAmount,
        slippagePercentDecimals: form.slippagePercentDecimals ?? percentUiToDecimals(DEFAULT_SLIPPAGE),
        inputAmount: 0,
        routeOverrideParams: undefined,
        clmmPosition: undefined,
        windType: LoopWindType.Collateral,
        skip: isRepayContext || form.slippagePercentDecimals === undefined
    });

    // could also maybe move this to the component and just render a cta error

    const repayLiquidationThreshold = getZcLoanLiquidationThreshold(loanExpanded);
    const increaseLeverageLiquidationThreshold = getZcLoanLiquidationThreshold(loanExpanded); // need to get from the new strategy

    const repayQuoteHealth = repayWithCollateralQuote
        ? calculateNewDebtAndCollateralAmounts({
              loanExpanded,
              isAddingLeverage: false,
              quote: repayWithCollateralQuote,
              liquidationThreshold: repayLiquidationThreshold
          })
        : undefined;

    // Validate health for increase leverage quote
    const increaseLeverageHealth = increaseLeverageQuote
        ? calculateNewDebtAndCollateralAmounts({
              loanExpanded,
              isAddingLeverage: true,
              quote: increaseLeverageQuote,
              liquidationThreshold: increaseLeverageLiquidationThreshold
          })
        : undefined;

    const validHealthRepayQuote = repayQuoteHealth?.newHealth ? repayQuoteHealth.newHealth > MIN_HEALTH_RATIO : true;

    const validHealthIncreaseLeverageQuote = increaseLeverageHealth?.newHealth
        ? increaseLeverageHealth.newHealth > MIN_HEALTH_RATIO
        : true;

    const executionDetailsInputs = getExecutionDetailsInputs({
        principalMetadata: loanExpanded?.principalMetadata,
        increaseLeverageQuote,
        increasePrincipalAmount,
        repayWithCollateralQuote,
        repayWithCollateralAmount,
        collateral,
        isIncreaseLeverage: !repayWithCollateralAmount,
        principalUsdPrice: loanExpanded?.principalUsdPrice,
        collateralUsdPrice: collateral?.usdPrice
    });

    return {
        // full repay quote
        fullRepayWithCollateralQuote,
        fullRepayWithCollateralQuoteLoading:
            fullRepayWithCollateralQuoteLoading || fullRepayWithCollateralQuoteIsFetching,
        fullRepayWithCollateralQuoteError: !!fullRepayWithCollateralQuoteError,

        // custom repay quote
        repayWithCollateralQuote,
        repayWithCollateralQuoteLoading: repayWithCollateralQuoteLoading || repayWithCollateralQuoteIsFetching,
        repayWithCollateralQuoteError:
            !repayWithCollateralQuoteLoading && !repayWithCollateralQuoteIsFetching
                ? repayWithCollateralQuoteError
                : undefined,

        // increase leverage quote
        increaseLeverageQuote: increaseLeverageQuote,
        increaseLeverageQuoteLoading: increaseLeverageQuoteLoading || increaseLeverageQuoteIsFetching,
        increaseLeverageQuoteError:
            !increaseLeverageQuoteLoading && !increaseLeverageQuoteIsFetching ? increaseLeverageQuoteError : undefined,
        validHealthRepayQuote,
        validHealthIncreaseLeverageQuote,
        executionDetailsInputs
    };
}
interface NewDebtAndCollateralAmounts {
    newDebtAmount: number | undefined;
    newDebtUsd: number | undefined;
    newCollateralAmount: number | undefined;
    newCollateralUsd: number | undefined;
    newHealth: number | undefined;
}

// audit right values
export function calculateNewDebtAndCollateralAmounts({
    loanExpanded,
    isAddingLeverage,
    quote,
    liquidationThreshold
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    isAddingLeverage: boolean;
    quote: LoopRouteResponseExpanded | undefined;
    liquidationThreshold: number | undefined;
}): NewDebtAndCollateralAmounts {
    const collateral = getZcCollateral(loanExpanded);
    const currentDebt = getZcLedger(loanExpanded)?.ledgerDebt.total ?? 0;
    const collateralAmount = collateral?.amount ?? 0;

    if (isAddingLeverage) {
        const debtToBorrow = lamportsToUiAmount(
            quote?.inputAmount ?? 0,
            loanExpanded?.principalMetadata?.decimals ?? 0
        );
        const collateralToReceive = lamportsToUiAmount(
            quote?.minOutputAmount ?? 0,
            collateral?.metadata?.decimals ?? 0
        );

        const newDebtAmount = bsMath.add(currentDebt, debtToBorrow);
        const newCollateralAmount = bsMath.add(collateralAmount, collateralToReceive);

        const newDebtUsd = bsMath.mul(newDebtAmount, loanExpanded?.principalUsdPrice ?? 0);

        const newCollateralUsd = bsMath.mul(newCollateralAmount, collateral?.usdPrice ?? 0);

        const newHealth = calculateHealthRatio(newDebtUsd, newCollateralUsd, liquidationThreshold);

        return {
            newDebtAmount,
            newDebtUsd,
            newCollateralUsd,
            newCollateralAmount,
            newHealth
        };
    } else {
        // Reducing leverage: sell collateral, repay debt
        const collateralSold = lamportsToUiAmount(quote?.inputAmount ?? 0, collateral?.metadata?.decimals ?? 0);
        const debtRepaid = lamportsToUiAmount(
            quote?.minOutputAmount ?? 0,
            loanExpanded?.principalMetadata?.decimals ?? 0
        );

        const newDebtAmount = Math.max(bsMath.sub(currentDebt, debtRepaid) ?? 0, 0);

        const newCollateralAmount = bsMath.sub(collateralAmount, collateralSold);

        const newDebtUsd = bsMath.mul(newDebtAmount, loanExpanded?.principalUsdPrice ?? 0);
        const newCollateralUsd = bsMath.mul(newCollateralAmount, collateral?.usdPrice ?? 0);

        const newHealth = calculateHealthRatio(newDebtUsd, newCollateralUsd, liquidationThreshold);

        return {
            newDebtAmount,
            newDebtUsd,
            newCollateralUsd,
            newCollateralAmount,
            newHealth
        };
    }
}

interface ExecutionDetailsInputsParams {
    increaseLeverageQuote: LoopRouteResponseExpanded | undefined;
    repayWithCollateralQuote: LoopRouteResponseExpanded | undefined;
    repayWithCollateralAmount: number | undefined;
    collateral: LoanCollateralExpanded | undefined;
    increasePrincipalAmount: number | undefined;
    isIncreaseLeverage: boolean;
    principalUsdPrice: number | undefined;
    collateralUsdPrice: number | undefined;
    principalMetadata: TokenListMetadata | undefined;
}

interface ExecutionDetailsInputs {
    externalQuote: LoopRouteResponseExpanded | undefined;
    receiveUsdAmount: number | undefined;
    leftOverPrincipalUsd: number | undefined;
    fairMarketValue: number | undefined;
    displayTokenType: LoopWindType | undefined;
}

function getExecutionDetailsInputs({
    increaseLeverageQuote,
    repayWithCollateralQuote,
    repayWithCollateralAmount,
    collateral,
    increasePrincipalAmount,
    isIncreaseLeverage,
    principalUsdPrice,
    collateralUsdPrice,
    principalMetadata
}: ExecutionDetailsInputsParams): ExecutionDetailsInputs {
    if (isIncreaseLeverage) {
        const increasePrincipalAmountUsd = bsMath.mul(increasePrincipalAmount, principalUsdPrice);
        const fairMarketCollateralAmount = bsMath.div(increasePrincipalAmountUsd, collateralUsdPrice);
        const receiveCollateralAmount = lamportsToUiAmount(
            increaseLeverageQuote?.minOutputAmount,
            collateral?.metadata?.decimals
        );

        const receiveCollateralAmountUsd = bsMath.mul(receiveCollateralAmount, collateralUsdPrice);

        return {
            externalQuote: increaseLeverageQuote,
            receiveUsdAmount: receiveCollateralAmountUsd,
            leftOverPrincipalUsd: 0,
            fairMarketValue: fairMarketCollateralAmount,
            displayTokenType: LoopWindType.Collateral
        };
    } else {
        const repayWithCollateralAmountUsd = bsMath.mul(repayWithCollateralAmount, collateralUsdPrice);
        const fairMarketPrincipalAmount = bsMath.div(repayWithCollateralAmountUsd, principalUsdPrice);

        const receivePrincipalToRepayAmount = lamportsToUiAmount(
            repayWithCollateralQuote?.minOutputAmount,
            principalMetadata?.decimals
        );

        const receivePrincipalToRepayAmountUsd = bsMath.mul(receivePrincipalToRepayAmount, principalUsdPrice);

        return {
            externalQuote: repayWithCollateralQuote,
            receiveUsdAmount: receivePrincipalToRepayAmountUsd,
            leftOverPrincipalUsd: 0,
            fairMarketValue: fairMarketPrincipalAmount,
            displayTokenType: LoopWindType.Principal
        };
    }
}
